/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
    HttpClient,
    HttpErrorResponse,
    HttpHeaders,
} from '@angular/common/http';
import { EventEmitter, Injectable, Injector } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { safeParse } from '../common/json.utils';
import { BasicAlertComponent } from '../components/alerts/basic-alert.component';
import { ActivityIndicatorModalComponent } from '../components/modals/activity-indicator-modal.component';
import { ErrorService } from '../services/error.service';
import { BrowserService } from './browser.service';
import { LoginService } from './login.service';
import { XdaStringsService } from './xda-strings.service';
import { BasicModalComponent } from '../components/modals/basic-modal.component';

@Injectable({
  providedIn: 'root',
})
export class UserService implements CanActivate {
  public user: User | null;
  public platform = Platform.WEBPORTAL;
  public isGuestUser = false;
  public curAGVersion: string;
  userAuthenticated: EventEmitter<User>;
  userLoggedOff: EventEmitter<boolean>;
  userLoginFailed: EventEmitter<boolean>;
  discovered: EventEmitter<boolean>;
  navigatingAway = false;

  get isAdvancedPlan(): boolean {
    if (this.user?.acquisition) {
      return this.user
        ? this.user.acquisition.productPlanTier >= ProductPlanTier.Advanced
        : false;
    }
    return false;
  }

  get useAuthCodes(): boolean {
    return this.xdaStringsService.strings?.['USE_AUTH_CODES']?.toLowerCase() === "true" ;
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    public xdaStringsService: XdaStringsService,
    private modalService: NgbModal,
    private errorService: ErrorService,
    private browserService: BrowserService,
    private injector: Injector,
  ) {
    const userCookie = this.getCookie('__Secure-user');
    this.user = !this.browserService.isLexmark && userCookie
      ? safeParse(userCookie)
      : null;
    this.userAuthenticated = new EventEmitter();
    this.userLoggedOff = new EventEmitter();
    this.userLoginFailed = new EventEmitter();
    this.discovered = new EventEmitter();
  }

  login(username: string, password: string) {
    return this.http
      .post<string>('api/account/login', { username, password })
      .toPromise()
      .then((resp) => {
        this.modalService.open(ActivityIndicatorModalComponent, {
          windowClass: 'xrx-modal transparent',
          centered: true,
          backdrop: 'static',
        });

        this.isGuestUser = false;
        this.setUserAndNotify(resp);
        return Promise.resolve(resp);
      })
      .catch((resp) => {
        this.userLoginFailed.emit(true);
        return Promise.reject(resp.error);
      });
  }

  async refreshToken(token: string) {
    try {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      };
      const user = await this.http
        .post<User>(
          'api/account/refreshAndDiscover',
          JSON.stringify(token),
          httpOptions,
      )
        .toPromise();
      this.setUserAndNotifyDiscovered(user);
    } catch (error) {
      if ((error as HttpErrorResponse).error !== undefined) {
        const err = error as HttpErrorResponse;
        const errorMessage = err.error?.error;
        if (errorMessage == 'eula-required') {
          this.displayEula(err, token);
        } else if (errorMessage == 'subscription-expired') {
          const errorDesc = JSON.parse(err.error?.errorDescription);
          const user = errorDesc.user;
          user.isSubscriptionExpired = true;
          this.setUserAndNotify(JSON.stringify(errorDesc.user));
        } else {
          this.displayRefreshError(errorMessage);
        }
      } else {
        this.errorService.displayGeneralError().result.then(() => {
          this.modalService.dismissAll();
        });
      }
    }
  }

  async createAuthCodeRedirect(agBaseUrl: string) {
    try {
      const authCode = (await this.http.get<string>('api/account/createAuthCode').toPromise());
      const encodedAppId = encodeURIComponent(this.xdaStringsService.strings["APPID"]);
      const encodedAuthCode = encodeURIComponent(authCode);
      const redirectUrl = `${agBaseUrl}?appId=${encodedAppId}&authCode=${encodedAuthCode}`;;
      return redirectUrl;
    }
    catch (error) {
      console.error(error);
      this.errorService.displayGeneralError().result.then(() => {
        this.modalService.dismissAll();
      });
    }
    return "";
  }

  async exchangeAuthCode(authCode: string) {
    try {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      };
      const userJson = (await this.http.post<User>('api/account/exchangeAuthCode', JSON.stringify(authCode), httpOptions).toPromise());
      this.setUserAndNotifyDiscovered(userJson);
    }
    catch (error) {
      if ((error as HttpErrorResponse).error !== undefined) {
        const err = error as HttpErrorResponse;
        const errorMessage = err.error?.error;
        if (errorMessage == 'eula-required') {
          this.displayEula(err);
        } else if (errorMessage == 'subscription-expired') {
          const errorDesc = JSON.parse(err.error?.errorDescription);
          const user = errorDesc.user;
          user.isSubscriptionExpired = true;
          this.setUserAndNotify(JSON.stringify(errorDesc.user));
        } else {
          this.displayRefreshError(errorMessage);
        }
      } else {
        this.errorService.displayGeneralError().result.then(() => {
          this.modalService.dismissAll();
        });
      }
    }
  }

  displayEula(err: HttpErrorResponse | null = null, token: string = "") {
    this.modalService.dismissAll();
    // Store whether user was logged in before showing EULA
    const wasLoggedIn = this.isLoggedOn();
    const modalRef = this.modalService.open(BasicModalComponent, {
      windowClass: 'xrx-modal',
      size: 'md',
    });

    modalRef.componentInstance.title = "SDE_UPDATED_TERMS_AVAILABLE";
    modalRef.componentInstance.body = this.xdaStringsService.strings[
      'SDE_MUST_LOG_AGAIN'
    ].replace('{0}', this.xdaStringsService.strings['APPNAME']);
    modalRef.componentInstance.okButtonText = 'SDE_LOG_IN';
    modalRef.componentInstance.showCancelHeaderButton = false;
    
    modalRef.result.then(
      () => {
        // User clicked OK - dismiss and initiate login
        this.modalService.dismissAll();
        this.injector.get(LoginService).initiateWebLogin();
      },
      () => {
        // User clicked cancel on EULA
        if (wasLoggedIn) {
          // User was already logged in, force complete logout
          this.user = null;
          this.setCookie('__Secure-user', '', -7);
          sessionStorage.removeItem('refreshToken');
          this.modalService.dismissAll('');
          this.redirectToHome();
          this.userLoggedOff.emit(true);
        } else {
          // User was not logged in, just clear any partial state
          this.logOff();
        }
      }
    );
  }

  displayRefreshError(error: string) {
    let message: string;
    switch (error) {
      case 'subscription-not-found':
        message = 'SDE_ACCOUNT_NOT_ASSOCIATED';
        break;
      case 'invalid-token':
      case 'server-error':
      default:
        message = 'SDE_OPERATION_FAILED_PLEASE2';
    }
    const modalRef = this.modalService.open(BasicAlertComponent);
    modalRef.componentInstance.title = message;
    modalRef.result.then(() => {
      this.modalService.dismissAll();
    });
  }

  displaySubscriptionExpiredAlert(specialStatusCode?: string, isTrial: boolean = false) {
    const modalRef = this.modalService.open(BasicAlertComponent);
    modalRef.componentInstance.title = isTrial ? 'SDE_TRIAL_EXPIRED' : 'SDE_SUBSCRIPTION_APPLICATION_EXPIRED';
    modalRef.componentInstance.buttonText = 'SDE_CLOSE';
    modalRef.componentInstance.extraClasses = 'three-buttons';

    if (specialStatusCode == 'AllocationOutOfUsageParent') {
      modalRef.componentInstance.title = 'SDE_ACCOUNT_NO_AVAILABLE';
      modalRef.componentInstance.body = 'SDE_SUBSCRIPTION_STILL_ACTIVE';
      modalRef.componentInstance.extraButtonText = 'SDE_MANAGE_USERS';
      modalRef.componentInstance.extraButtonText2 = 'SDE_MANAGE_SUBSCRIPTION';
    } else if (specialStatusCode == 'AllocationOutOfUsageChild') {
      modalRef.componentInstance.title = 'SDE_SUBSCRIPTION_EXPIRED';
      modalRef.componentInstance.body = 'SDE_TO_RENEW_SUBSCRIPTION';
      modalRef.componentInstance.extraButtonText = 'SDE_MANAGE_USERS';
    } else if (this.user && this.user.accessListRole === AccessListRole.Owner) {
      modalRef.componentInstance.body = 'SDE_PURCHASE_NEW_SUBSCRIPTION';
      modalRef.componentInstance.extraButtonText = 'SDE_MANAGE_USERS';
      modalRef.componentInstance.extraButtonText2 = 'SDE_PURCHASE_SUBSCRIPTION';
    } else {
      modalRef.componentInstance.body = 'SDE_CONTACT_SUBSCRIPTION_OWNER';
    }

    modalRef.result.then((data) => {
      if (data === 'Extra2') {
        this.redirect('purchase');
        this.logOff();
      } else if (data === 'Extra') {
        this.modalService.dismissAll();
        this.router.navigate(['/access-list']);
      }
      else {
        this.logOff();
      }
    });
  }

  setUser(userJson: string | User) {
    this.user = typeof userJson === 'string' ? JSON.parse(userJson) : userJson;
    if (!this.isGuestUser && this.platform != Platform.MFD) {
      this.setCookie('__Secure-user', typeof userJson === 'string' ? userJson : JSON.stringify(userJson));
      sessionStorage.setItem(
        'refreshToken',
        this.user?.refresh_token ? this.user?.refresh_token : '',
      );
    }
  }

  setUserAndNotify(userJson: string, isRefresh = false) {
    const accessListId = this.user?.accessListId;
    this.setUser(userJson);

    if (isRefresh && accessListId && this.user) {
      this.user.accessListId = accessListId;
    }

    this.userAuthenticated.emit(this.user ? this.user : undefined);
  }

  setUserAndNotifyDiscovered(user: User) {
    // If we were discovered than we are a subscription user
    user.isSubscriptionUser = true;
    this.user = user;
    if (!this.isGuestUser && this.platform != Platform.MFD) {
      this.setCookie('__Secure-user', JSON.stringify(user));
      sessionStorage.setItem('refreshToken', this.user?.refresh_token);
    }
    this.discovered.emit(true);
  }

  setCookie(name: string, value: string, days?: number) {
    // if days is not provided, default to the app setting value, or 30 if that is not set
    const cookieAge =
      (days ??
        Number(this.xdaStringsService.strings['COOKIE_EXPIRATION_DAYS'])) ||
      30;
    const date = new Date();
    date.setDate(date.getDate() + cookieAge);
    document.cookie = `${name}=${value};path=/;expires=${date.toUTCString()};secure;samesite=lax`;
  }

  getCookie(name: string): string {
    const cookieValue = document.cookie
      .split('; ')
      .find((c) => c.startsWith(`${name}=`))
      ?.split('=')[1];
    return cookieValue ?? '';
  }

  getAccessToken() {
    return this.user ? this.user.access_token : '';
  }

  getRefreshToken() {
    if (this.platform == Platform.MFD) {
      return this.user ? this.user.refresh_token : '';
    } else {
      return (
        sessionStorage.getItem('refreshToken') || this.user?.refresh_token || ''
      );
    }
  }

  refreshTokenObs() {
    // If we have a refresh token, refresh the access token
    if (this.getRefreshToken() != '') {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'X-Skip-Interceptor': '',
        }),
      };
      return this.http.post<string>(
        'api/account/refreshToken',
        JSON.stringify(this.getRefreshToken()),
        httpOptions,
      );
    } else {
      return new Observable<string>((observer) => {
        return observer.error();
      });
    }
  }

  isLoggedOn(): boolean {
    return this.user ? true : false;
  }

  logOff(redirect = true) {
    // Try to refresh token first, then call logout API (fire-and-forget)
    this.refreshTokenBeforeLogout().then(() => {
      // Token refreshed, call logout API
      this.http.get('api/account/logout').subscribe();
    }).catch(() => {
      // Refresh failed, still try logout API
      this.http.get('api/account/logout').subscribe();
    });

    if (this.user?.authType) {
      this.appGalleryCloudLogout();
    }
    this.user = null;
    this.setCookie('__Secure-user', '', -7);
    this.modalService.dismissAll('');
    if (redirect) {
      this.redirectToHome();
    }
    this.userLoggedOff.emit(true);
  }

  private async refreshTokenBeforeLogout(): Promise<void> {
    if (this.getRefreshToken()) {
      await this.refreshTokenObs().toPromise();
    }
  }

  appGalleryCloudLogout() {
    const agBaseUrl = this.xdaStringsService.strings['AGBASEURL'];
    const appBaseUrl = this.xdaStringsService.strings['APPBASEURL'];
    const url = `${agBaseUrl}/cloudauth/logout_redirect?email=${encodeURIComponent(this.user?.email || '')}&redirect_url=${encodeURIComponent(appBaseUrl)}`;
    window.location.href = url;
  }

  redirectToHome() {
    if (this.router.url.includes('mfd')) {
      this.router.navigate(['/mfd']);
    } else {
      this.router.navigate(['/']);
    }
  }

  setAccessListId(accessListId: string) {
    if (this.user) {
      this.user.accessListId = accessListId;
      this.updateUser();
    }
  }

  updateUser() {
    if (this.user) {
      this.setUser(JSON.stringify(this.user));
    }
  }

  canActivate(): boolean {
    if (!this.isLoggedOn()) {
      this.redirectToHome();
      return false;
    }
    return true;
  }

  getLoginError(err: string): string {
    switch (err) {
      case 'account_locked': {
        const error = this.xdaStringsService.strings[
          'SDE_ACCOUNT_LOCKED_DUE1'
        ].replace('{0}', '5');
        const modalRef = this.modalService.open(BasicAlertComponent);
        modalRef.componentInstance.title = error;
        modalRef.componentInstance.body = 'SDE_EMAIL_SENT_ACCOUNT';
        modalRef.result.then(() => {
          this.modalService.dismissAll();
        });
        return error;
      }
      case 'invalid_tos':
        return 'SDE_MUST_AGREE_UPDATED';
      case 'account_suspended':
        return 'SDE_ACCOUNT_SUSPENDED';
      case 'account_inactive':
        return 'SDE_ACCOUNT_NOT_ACTIVATED';
      case 'invalid_grant':
        return 'SDE_EMAIL_ANDOR_PASSWORD';
      case 'account_BlackListed':
        return 'SDE_EMAIL_ADDRESS_CANNOT3';
      case 'account_scheduled_for_delete':
        return 'SDE_MADE_REQUEST_DELETE';
      case 'account_validating':
        return 'SDE_ACCOUNT_MUST_BE';
      case 'email_validating':
        return 'SDE_EMAIL_MUST_BE';
      case 'account_rejected':
        return 'SDE_ACCOUNT_LOCKED';
      case 'account_needsreview':
        return 'SDE_ACCOUNT_UNDER_REVIEW';
      case 'server_error':
      default:
        this.errorService.displayGeneralError().result.then(() => {
          this.modalService.dismissAll();
        });
        return 'SDE_SYSTEM_ENCOUNTERED_ERROR1';
    }
  }

  shouldDisplayCredits() {
    // We should display credits if the user is logged on, a subscription user, not a guest, and have valid acquisition credits
    const result =
      this.isLoggedOn() && this.user?.isSubscriptionUser && !this.isGuestUser;

    // Acquisition credits are valid if they have usage credits, and they are not -1;
    return result && this.user?.acquisition && this.user?.acquisition?.usage;
  }

  guestLogin() {
    this.modalService.open(ActivityIndicatorModalComponent, {
      windowClass: 'xrx-modal transparent',
      centered: true,
    });

    return this.http
      .get('api/account/guestLogin')
      .toPromise()
      .then((resp) => {
        this.isGuestUser = true;
        this.setUserAndNotify(JSON.parse(resp.toString()));
        return Promise.resolve(resp);
      })
      .catch((resp) => {
        this.userLoginFailed.emit(true);
        return Promise.reject(resp.error);
      });
  }

  async isAuthTokenValid(): Promise<boolean> {
    try {
      const resp = await this.http
        .get<boolean>('api/account/validate-authorization')
        .toPromise();
      return resp;
    } catch {
      return false;
    }
  }

  editAGAccountIfRequired() {
    if (this.user?.userInfoRequired) {
      const modalRef = this.modalService.open(BasicAlertComponent);
      modalRef.componentInstance.title = 'SDE_ACCOUNT_MISSING_SOME';
      modalRef.componentInstance.buttonText = 'SDE_CLOSE';
      modalRef.result.then(async () => {
        const agBaseUrl = this.xdaStringsService.strings['AGBASEURL'];
        let redirectUrl = "";
        if (this.useAuthCodes) {
          const baseUrl = `${agBaseUrl}#!/edit-account`;
          redirectUrl = await this.createAuthCodeRedirect(baseUrl);
        } else {
          const appId = this.xdaStringsService.strings['APPID'];
          const refreshToken = this.getRefreshToken();
          redirectUrl = `${agBaseUrl}#!/edit-account?appId=${appId}&refreshToken=${refreshToken}`;
        }
        window.location.href = redirectUrl;
      });
    }
  }

  validateEntitlement() {
    this.http.get('api/account/validate-entitlement').subscribe(
      (entitlement: unknown) => {
        if ((entitlement as Entitlement).isEntitled !== undefined) {
          const entitlementObj = entitlement as Entitlement;
          // if a user is not entitled, log them out
          if (!entitlementObj.isEntitled) {
            this.logOff();
          }
        }
      },
      (err) => {
        console.log(err);
      },
    );
  }

  async getEntitlement(): Promise<Entitlement | null> {
    try {
      let resp = await this.http.get<Entitlement>('api/account/validate-entitlement').toPromise();
      return resp;
    }
    catch {
      return null;
    }
  }

  async redirect(action: string) {
    const agBaseUrl = this.xdaStringsService.strings['AGBASEURL'];
    let windowUrl = "";
    if (this.useAuthCodes) {
      const baseUrl = `${agBaseUrl}#!/subscription`;
      const redirectUrl = await this.createAuthCodeRedirect(baseUrl);
      windowUrl = `${redirectUrl}&action=${action}`;
    } else {
      const appId = this.xdaStringsService.strings['APPID'];
      const user = JSON.stringify(this.user);
      windowUrl = `${agBaseUrl}#!/subscription?appId=${appId}&user=${encodeURIComponent(user)}&action=${action}`;
    }
    window.open(windowUrl);
  }

  async getAGVersion() {
    if (!this.curAGVersion) {
      try {
        this.curAGVersion = await this.http
          .get<string>('api/account/get-ag-version')
          .toPromise();
      } catch {
        this.curAGVersion =
          this.xdaStringsService.strings['AG_VERSION_AT_RELEASE'];
      }
    }
    return this.curAGVersion;
  }
}

export interface User {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: string[];
  company: string;
  country_code: string;
  countryName: string;
  language: string;
  currency_id: string;
  currency: Currency;
  affiliateId: string;
  devCode: string;
  id: string;
  showCreditCards: boolean;
  '.issued': string;
  '.expires': string;
  appReturnUrl: string;
  accessListId: string;
  accessListRole: AccessListRole;
  isSubscriptionExpired: boolean;
  isSubscriptionUser?: boolean;
  bvdEntityType: string;
  bvdEntityTypeID: string;
  userInfoRequired: boolean;
  authType: string;
  error: string | null;
  errorDescription: string | null;
  acquisition: Acquisition | null;
}

export interface Usage {
  unitName: string;
  unitsPurchased: number;
  unitsRemaining: number;
  unitsUsed: number;
}

export interface Acquisition {
  productPlanName: string;
  licenseStartDate: string;
  licenseExpirationDate: string;
  acquisitionId: string;
  isExpired: boolean;
  outOfUsage: boolean;
  usage: Usage;
  subscriptionId: string;
  isEnterprise: boolean;
  productPlanTier: ProductPlanTier;
  showRenewNotice: boolean;
  specialStatusCode: number;
}

export enum AccessListRole {
  Owner = 'Owner',
  Manager = 'Manager',
  User = 'User',
}

export enum ProductPlanTier {
  None,
  Starter,
  Advanced,
  Premium,
}

export enum UserStatus {
  Active = 'Active',
  Invited = 'Invited',
  Suspended = 'Suspended',
  Locked = 'Locked',
  DeleteRequested = 'DeleteRequested',
  RejectedBySystem = 'RejectedBySystem',
  RejectedByAdmin = 'RejectedByAdmin',
}

export interface Currency {
  CurrencyId: string;
  CurrencyCode: string;
  Name: string;
  LocalizationKey: string;
}

export enum Platform {
  WEBPORTAL,
  MFD,
}

export interface Entitlement {
  remainingDays: number;
  usage: {
    consumed: number;
    unit: string;
    remaining: number;
  };
  isEntitled: boolean;
  entitledStatusCode: number;
}

export enum EntitlementSpecialStatusCode {
  None = 0,
  AllocationOutOfUsageParent = 1,
  AllocationOutOfUsageChild = 2
}
